import clsx from 'clsx';
import React from 'react';
import { useDataTable } from '../DataTable';
import ItemsPerPageSelector from './ItemsPerPageSelector';

interface DataTablePaginationProps {
  showPerPage?: boolean;
}

const DataTablePagination: React.FC<DataTablePaginationProps> = ({ showPerPage = true }) => {
  const { table, pageInfo, pagination } = useDataTable();

  if (!table || table.getRowModel().rows.length === 0) return null;

  const totalPages = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex;
  const isFirstPage = currentPage === 0;
  const isLastPage = currentPage === totalPages - 1;

  const getPageNumbers = (): number[] => {
    const maxPagesToShow = 5;
    const halfWay = Math.floor(maxPagesToShow / 2);

    let start = Math.max(currentPage - halfWay, 0);
    let end = start + maxPagesToShow;

    if (end > totalPages) {
      end = totalPages;
      start = Math.max(end - maxPagesToShow, 0);
    }

    return Array.from({ length: totalPages >= maxPagesToShow ? maxPagesToShow : totalPages }, (_, i) => start + i);
  };

  return (
    <div className="row">
      {!!showPerPage && (
        <ItemsPerPageSelector
          label={pagination?.label}
          options={pagination?.pageSizes}
          pageItemCount={pageInfo?.pageItemCount}
          table={table}
        />
      )}
      <ul
        className={clsx(
          'pagination col-sm-12 d-flex align-items-center justify-content-center',
          showPerPage ? 'col-md-7 justify-content-md-end' : 'col-md-12 justify-content-md-center',
        )}
      >
        <li className={clsx('page-item previous', { disabled: isFirstPage })}>
          <a
            aria-disabled={isFirstPage}
            aria-label="Página anterior"
            className="page-link"
            href="#"
            tabIndex={isFirstPage ? -1 : undefined}
            onClick={(e) => {
              e.preventDefault();
              if (!isFirstPage) table.setPageIndex(currentPage - 1);
            }}
          >
            <i aria-hidden="true" className="previous" />
          </a>
        </li>
        {getPageNumbers().map((pageNumber) => (
          <li key={pageNumber} className={clsx('page-item', { active: pageNumber === currentPage })}>
            <a
              aria-current={pageNumber === currentPage ? 'page' : undefined}
              className={clsx('page-link', { 'text-gray-600': pageNumber !== currentPage })}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                table.setPageIndex(pageNumber);
              }}
            >
              {pageNumber + 1}
            </a>
          </li>
        ))}
        <li className={clsx('page-item next', { disabled: isLastPage })}>
          <a
            aria-disabled={isLastPage}
            aria-label="Próxima página"
            className="page-link"
            href="#"
            tabIndex={isLastPage ? -1 : undefined}
            onClick={(e) => {
              e.preventDefault();
              if (!isLastPage) table.setPageIndex(currentPage + 1);
            }}
          >
            <i aria-hidden="true" className="next" />
          </a>
        </li>
      </ul>
    </div>
  );
};

export default DataTablePagination;
